import React, { useMemo } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    XAxis,
    YAxis
} from 'recharts';
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent
} from '@components/ui/chart';
import { EnrichedTrack, SelectedWorkout } from '../types';

export type MetricKind = 'speed' | 'acceleration' | 'elevation';

interface MetricChartProps {
    kind: MetricKind;
    title: string;
    yLabel: string;
    tracks: EnrichedTrack[];
    selectedWorkouts: SelectedWorkout[];
    progress: number;
    onProgressChange: (progress: number) => void;
    height?: number;
}

const valueForKind = (
    p: EnrichedTrack['points'][number],
    kind: MetricKind
): number | null => {
    if (kind === 'speed') {
        const speed = p.speed !== null ? p.speed : p.computedSpeed;
        return speed !== null ? speed * 3.6 : null;
    }
    if (kind === 'acceleration') return p.acceleration;
    return p.ele;
};

export function MetricChart({
    kind,
    title,
    yLabel,
    tracks,
    selectedWorkouts,
    progress,
    onProgressChange,
    height = 160
}: MetricChartProps) {
    const series = useMemo(
        () =>
            tracks.map((track) => {
                const sw = selectedWorkouts.find(
                    (w) => w.id === track.workoutId
                );
                const n = track.points.length;
                return {
                    key: `track_${track.workoutId}`,
                    label: sw?.label ?? `#${track.workoutId}`,
                    color: sw?.color ?? 'var(--chart-1)',
                    data: track.points.map((p, i) => ({
                        x: n > 1 ? (i / (n - 1)) * 100 : 0,
                        y: valueForKind(p, kind)
                    }))
                };
            }),
        [tracks, selectedWorkouts, kind]
    );

    const config = useMemo<ChartConfig>(
        () =>
            Object.fromEntries(
                series.map((s) => [s.key, { label: s.label, color: s.color }])
            ),
        [series]
    );

    if (tracks.length === 0) {
        return (
            <div
                style={{ height }}
                className="flex items-center justify-center rounded-lg border bg-card text-xs text-muted-foreground"
            >
                {title}: no data
            </div>
        );
    }

    return (
        <ChartContainer
            config={config}
            style={{ height }}
            className="aspect-auto w-full"
        >
            <LineChart
                margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
                onClick={(state) => {
                    const x = Number(state?.activeLabel);
                    if (Number.isNaN(x)) return;
                    onProgressChange(Math.max(0, Math.min(100, x)) / 100);
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="x"
                    type="number"
                    domain={[0, 100]}
                    tickCount={6}
                    tickFormatter={(v) => `${v}%`}
                    tickLine={false}
                    axisLine={false}
                />
                <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={48}
                    label={{
                        value: yLabel,
                        angle: -90,
                        position: 'insideLeft',
                        className: 'fill-muted-foreground'
                    }}
                />
                <ChartTooltip
                    content={
                        <ChartTooltipContent
                            indicator="line"
                            labelFormatter={(label) =>
                                `${title} @ ${Number(label).toFixed(1)}%`
                            }
                        />
                    }
                />
                {series.map((s) => (
                    <Line
                        key={s.key}
                        name={s.key}
                        data={s.data}
                        dataKey="y"
                        type="monotone"
                        stroke={s.color}
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls
                        isAnimationActive={false}
                    />
                ))}
                <ReferenceLine
                    x={progress * 100}
                    stroke="var(--foreground)"
                    strokeDasharray="3 3"
                />
            </LineChart>
        </ChartContainer>
    );
}
